import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import lottie, {AnimationItem} from 'lottie-web';
import {Subject, takeUntil} from 'rxjs';
import {
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule
} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList} from '@angular/cdk/drag-drop';
import {ConnectedOverlayPositionChange, OverlayModule} from '@angular/cdk/overlay';
import {
  AI_BUBBLE_POSITIONS_START,
  isBubbleAbove
} from '../../components/ai-generation-bubble/ai-bubble-overlay';
import {MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipModule} from '@angular/material/tooltip';
import {TOOLTIP_BASE_OPTIONS} from '../../helpers/tooltip-defaults';
import {AutoSelectInputDirective} from '../../directives/auto-select-input.directive';
import {MetaData} from '../../../api/interfaces/metadata';
import {MatDividerModule} from '@angular/material/divider';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatInputModule} from '@angular/material/input';
import {MatNativeDateModule} from '@angular/material/core';
import {AutoResizeInputDirective} from '../../directives/auto-resize-input.directive';
import {HasPackageDirective} from '../../../packages/has-package.directive';
import {AiStateService} from '../../../services/ai-state.service';
import {GenerationStateService} from '../../../services/generation-state.service';
import {PACKAGE_NAMES} from '@maporium-workspace/shared';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {SettingsService} from '../../../api/services/setting.service';
import {PackageService} from '../../../api/services/package.service';
import {hasAddedNewProperty, userEditedMetadata} from '../../helpers/signal.helpers';
import {
  AiGenerationBubbleComponent,
  AIGenerationBubbleConfig
} from '../../components/ai-generation-bubble/ai-generation-bubble.component';
import {
  AiBubbleStateService,
  AIGenerationBubbleSource
} from '../../components/ai-generation-bubble/ai-bubble-state.service';


@Component({
  selector: 'maporium-meta-data-control',
  templateUrl: './meta-data-control.component.html',
  styleUrls: ['./meta-data-control.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MetaDataControlComponent),
      multi: true
    },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: {
        ...TOOLTIP_BASE_OPTIONS,
        position: 'below'
      }
    }
  ],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    MatTooltipModule,
    AutoSelectInputDirective,
    MatDividerModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatInputModule,
    AutoResizeInputDirective,
    HasPackageDirective,
    TranslatePipe,
    AiGenerationBubbleComponent,
    OverlayModule
  ]
})
export class MetaDataControlComponent implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor {
  private static readonly GENERATE_LOTTIE_PATH = 'assets/lottie/symbol-generate.json';
  private generateLottieInstance: AnimationItem | null = null;
  private generateLottieReady = false;
  private pendingPlay = false;
  private destroy$ = new Subject<void>();

  @ViewChild('generateLottieEl', {read: ElementRef})
  generateLottieElRef?: ElementRef<HTMLElement>;
  @Input() sorting = true;
  @Input() forMap = false;
  @Output() addMeta = new EventEmitter<FormGroup>;
  @Output() extendMeta = new EventEmitter<void>();

  // AI Generation Bubble Properties
  public showAiBubble = false;
  public aiBubbleConfig?: AIGenerationBubbleConfig;
  public aiBubbleAbove = false;
  public readonly aiBubblePositions = AI_BUBBLE_POSITIONS_START;
  private longPressTimer?: number;
  private isLongPress = false;
  private readonly longPressDelay = 500; // 500ms for long press

  //@ts-ignore
  keyValueForm: FormGroup;

  selectedRowIndex: number | null = null;
  packageNames = PACKAGE_NAMES;
  aiBubbleSource = AIGenerationBubbleSource;
  private autoFocusTimer?: ReturnType<typeof setTimeout>;

  @ViewChildren('txtAreaKey') txtAreaKeys!: QueryList<ElementRef>;
  @ViewChildren('txtAreaValue') txtAreaValues!: QueryList<ElementRef>;
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const targetElement = event.target as HTMLElement;

    // Check if clicked outside of any of the rows
    if (!targetElement.closest('.pseudo-row')) {
      this.selectedRowIndex = null;
    }
  }


  get pairs(): FormArray {
    return this.keyValueForm.get('pairs') as FormArray;
  }

  get isDefaulter(): boolean {
    return this.packageService.isDefaulter();
  }

  constructor(private fb: FormBuilder,
              public aiStateService: AiStateService,
              private aiBubbleStateService: AiBubbleStateService,
              private settingsService: SettingsService,
              private packageService: PackageService,
              public generationStateService: GenerationStateService,
              private hostRef: ElementRef<HTMLElement>,
              private translate: TranslateService,
              private cdr: ChangeDetectorRef) {
  }

  /**
   * Checks if the user has Publisher or Developer package.
   * Publishers and Developers get full AI generation options (bubble).
   * Creators get limited AI (direct generation, no bubble).
   */
  private isPublisherOrDeveloper(): boolean {
    return this.packageService.hasPackage(PACKAGE_NAMES.publisher) ||
      this.packageService.hasPackage(PACKAGE_NAMES.analyst) ||
      this.packageService.hasPackage(PACKAGE_NAMES.developer);
  }

  ngOnInit(): void {
    this.keyValueForm = this.fb.group({
      pairs: this.fb.array([])
    });


    this.keyValueForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => userEditedMetadata.set(true));

    this.generationStateService.propertyGenerationInProgress$
      .pipe(takeUntil(this.destroy$))
      .subscribe((inProgress) => {
        if (inProgress) {
          this.playGenerateLottie();
        } else {
          this.pauseGenerateLottie();
        }
      });
  }

  ngAfterViewInit(): void {
    this.initGenerateLottie();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.destroyGenerateLottie();
  }

  private initGenerateLottie(): void {
    // The slot is gated by *appHasPackage — lower tiers don't render the
    // button at all, so the ViewChild may be undefined; that's fine.
    const container = this.generateLottieElRef?.nativeElement;
    if (!container) return;
    this.generateLottieInstance = lottie.loadAnimation({
      container,
      renderer: 'svg',
      loop: true,
      autoplay: false,
      path: MetaDataControlComponent.GENERATE_LOTTIE_PATH,
    });
    // Avoid a one-frame flash of the first animation pose on load by
    // deferring "ready" until lottie has the JSON and a frame queued.
    this.generateLottieInstance.addEventListener('DOMLoaded', () => {
      this.generateLottieReady = true;
      if (this.pendingPlay) {
        this.generateLottieInstance?.goToAndPlay(0, true);
        this.pendingPlay = false;
      }
    });
  }

  private playGenerateLottie(): void {
    if (!this.generateLottieInstance) return;
    if (!this.generateLottieReady) {
      this.pendingPlay = true;
      return;
    }
    this.generateLottieInstance.goToAndPlay(0, true);
  }

  private pauseGenerateLottie(): void {
    this.pendingPlay = false;
    this.generateLottieInstance?.stop();
  }

  private destroyGenerateLottie(): void {
    this.generateLottieInstance?.destroy();
    this.generateLottieInstance = null;
    this.generateLottieReady = false;
    this.pendingPlay = false;
  }

  selectRow(index: number, $event: MouseEvent, isEditable = true): void {
    if (isEditable === false) {
      return;
    }
    this.selectedRowIndex = index;
    $event.stopPropagation();
  }

  drop(event: CdkDragDrop<FormGroup[]>): void {
    const controls = this.pairs.controls;

    // Move the dragged item to its new position
    const draggedControl = controls[event.previousIndex];
    controls.splice(event.previousIndex, 1);
    controls.splice(event.currentIndex, 0, draggedControl);

    // Recalculate the 'order' for each control
    controls.forEach((control, index) => {
      control.patchValue({ order: index });
    });

    this.pairs.updateValueAndValidity();
    this.selectedRowIndex = event.currentIndex;
  }

  addPair(): void {
    // Signal that a new property row was added - prevents form re-patching from stealing focus
    hasAddedNewProperty.set(true);

    const meta = {
      id: undefined,
      key: '',
      value: '',
      type: 'string',
      order: this.pairs.length,
      editable: true
    };
    let pair;

    if (this.selectedRowIndex !== null) {
      // Determine the insertion index
      const index = this.selectedRowIndex - 1 === -1 ? 0 : this.selectedRowIndex;
      // Set the order for the new meta object
      meta.order = index + 1;
      pair = this.fb.group(meta);
      // Insert the new pair at the correct position
      this.pairs.insert(index + 1, pair);

      // Update the order of subsequent elements
      for (let i = index + 2; i < this.pairs.length; i++) {
        this.pairs.at(i).patchValue({ order: i });
      }

      this.handleAutoFocus(index + 1);
    } else {
      // If no row is selected, just append to the end
      meta.order = this.pairs.length;
      pair = this.fb.group(meta);
      this.pairs.push(pair);
      this.handleAutoFocus(this.pairs.length - 1);
    }
    this.addMeta.emit(pair);
  }

  /** Called when user starts typing in a key field - clears the hasAddedNewProperty flag */
  onKeyFieldInput(): void {
    hasAddedNewProperty.set(false);
  }


  removePair(): void {
    if (this.selectedRowIndex !== null) {
      this.pairs.removeAt(this.selectedRowIndex);
      this.selectedRowIndex = null;
      this.pairs.markAsTouched();
    }
  }

  extendExistingMetaWithValue(): void {
    this.extendMeta.emit();
  }

  getGenerateButtonTooltip(): string {
    if (!this.aiStateService.areAIFeaturesEnabled()) {
      return this.translate.instant('SHARED_FORMS.META_DATA_CONTROL.GENERATE_TOOLTIP_NO_API_KEY');
    }

    const longPressOnly = this.settingsService.getAiGenerationLongPressOnly();
    return longPressOnly
      ? this.translate.instant('SHARED_FORMS.META_DATA_CONTROL.GENERATE_TOOLTIP_LONG_PRESS')
      : this.translate.instant('SHARED_FORMS.META_DATA_CONTROL.GENERATE_TOOLTIP_DEFAULT');
  }

  onGenerateButtonMouseDown(event: MouseEvent): void {
    if (!this.aiStateService.areAIFeaturesEnabled()) {
      return;
    }

    this.isLongPress = false;
    this.longPressTimer = window.setTimeout(() => {
      this.isLongPress = true;
      this.showAIGenerationBubble();
    }, this.longPressDelay);
  }

  onGenerateButtonMouseUp(event: MouseEvent): void {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = undefined;
    }

    // Reset long press flag after a short delay to allow click event to see it
    setTimeout(() => {
      this.isLongPress = false;
    }, 50);
  }

  async onGenerateButtonClick(): Promise<void> {
    if (!(await this.aiStateService.waitForAIFeaturesEnabled())) {
      this.showAIConfigureBubble();
      return;
    }

    // If it was a long press, don't do anything - bubble is already shown
    if (this.isLongPress) {
      return;
    }

    // Respect longPressOnly setting
    const longPressOnly = this.settingsService.getAiGenerationLongPressOnly();
    if (longPressOnly) {
      // Start generation immediately with default options
      this.extendExistingMetaWithValue();
    } else {
      // Show the generation options bubble
      this.showAIGenerationBubble();
    }
  }

  get bubbleSource() {
    return this.aiBubbleStateService.getSource();
  }

  private showAIConfigureBubble(): void {
    this.aiBubbleConfig = {
      type: 'configure',
      hasDescription: false,
      hasProperties: false,
      hasEmptyProperties: false
    };
    this.showAiBubble = true;
    this.aiBubbleStateService.setSource(AIGenerationBubbleSource.sidebar);
  }

  private showAIGenerationBubble(): void {
    const currentMetadata = this.pairs.value || [];
    this.aiBubbleConfig = {
      type: 'generate',
      hasDescription: true,
      hasProperties: currentMetadata.length > 0,
      hasEmptyProperties: this.hasEmptyProperties(currentMetadata),
      forMap: this.forMap
    };
    this.showAiBubble = true;
    this.aiBubbleStateService.setSource(AIGenerationBubbleSource.sidebar);
  }


  private hasEmptyProperties(metadata: any[]): boolean {
    return metadata.some((meta: any) => !meta.value || meta.value.trim() === '');
  }

  onAiBubbleGenerate(): void {
    this.extendMeta.emit();
    this.showAiBubble = false;
    this.aiBubbleStateService.clearSource();
  }

  onAiBubbleOpenSettings(): void {
    this.showAiBubble = false;
    this.aiBubbleStateService.clearSource();
  }

  onAiBubbleClose(): void {
    if (!this.showAiBubble) return;
    this.showAiBubble = false;
    this.aiBubbleStateService.clearSource();
  }

  onAiBubblePositionChange(change: ConnectedOverlayPositionChange): void {
    this.aiBubbleAbove = isBubbleAbove(change);
  }

  onAiBubbleLongPressToggle(longPressOnly: boolean): void {
    this.settingsService.setAiGenerationLongPressOnly(longPressOnly);
  }

  writeValue(pairs: MetaData[] | {pairs: MetaData[]}): void {
    let pairsArray: MetaData[];
    if (Array.isArray(pairs)) {
      pairsArray = pairs;
    } else {
      pairsArray = pairs.pairs;
    }

    const current = this.pairs;

    // MAP-1531: same row count → patch controls in place instead of
    // setControl, which rebuilds the *ngFor and drops focus from the field
    // the user is typing in.
    if (current && current.length > 0 && current.length === pairsArray.length) {
      const focusedIndex = this.getFocusedRowIndex();
      if (focusedIndex === -1) {
        // Not an active edit (switch / undo / external sync) — clear stale selection.
        this.selectedRowIndex = null;
      }
      pairsArray.forEach((incoming, i) => {
        const ctrl = current.at(i);
        if (i === focusedIndex) {
          // Don't overwrite the field the user is editing — only sync id/order.
          this.setControlValue(ctrl, 'id', incoming.id);
          this.setControlValue(ctrl, 'order', incoming.order);
        } else {
          ctrl.patchValue(incoming, {emitEvent: false});
        }
      });
      return;
    }

    // Cancel any pending auto-focus from a previous addPair call —
    // the DOM elements it targets are about to be destroyed.
    if (this.autoFocusTimer) {
      clearTimeout(this.autoFocusTimer);
      this.autoFocusTimer = undefined;
    }

    // Reset selection — the old index is meaningless after a full rebuild.
    this.selectedRowIndex = null;

    const pairFGs = pairsArray.map(pair => this.fb.group(pair));
    const pairsFormArray = this.fb.array(pairFGs);
    this.keyValueForm.setControl('pairs', pairsFormArray, {emitEvent: false});
    // Force change detection to sync template with new FormArray
    // This prevents "Cannot find control" errors when undo reduces array length
    this.cdr.detectChanges();
  }

  /** Row index whose key/value textarea currently holds focus, or -1. */
  private getFocusedRowIndex(): number {
    const active = document.activeElement;
    if (!active) return -1;
    const keys = this.txtAreaKeys?.toArray() ?? [];
    for (let i = 0; i < keys.length; i++) {
      if (keys[i]?.nativeElement === active) return i;
    }
    const values = this.txtAreaValues?.toArray() ?? [];
    for (let i = 0; i < values.length; i++) {
      if (values[i]?.nativeElement === active) return i;
    }
    return -1;
  }

  private setControlValue(ctrl: any, field: string, value: any): void {
    const child = ctrl?.get(field);
    if (child && child.value !== value) {
      child.setValue(value, {emitEvent: false});
    }
  }

  registerOnChange(fn: any): void {
    this.keyValueForm.valueChanges.subscribe(fn);
  }

  registerOnTouched(fn: any): void {
    // Implement if needed
  }

  onEnter($event: KeyboardEvent): void {
    if ($event.key === 'Enter' || $event.keyCode === 13) {
      const target = $event.target as HTMLInputElement;
      if (target.classList.contains('key-input')) {
        // its a key dont do antything
        $event.preventDefault();
        return;
      }
    }
  }

  onTab($event: any, rowIndex: number, fieldType: string, isEditable = true): void {
    if (!isEditable) {
      return;
    }
    // If tabbing from the key field, just update the selected row.
    if (fieldType === 'key') {
      this.selectRow(rowIndex, $event);
      return;
    }

    // If tabbing from the value field and it's not the last row, go to the key field of the next row.
    if (fieldType === 'value' && rowIndex < this.pairs.length - 1) {
      $event.preventDefault(); // prevent default tabbing behavior.

      this.selectRow(rowIndex + 1, $event);

      setTimeout(() => {
        const nextKeyInput = document.getElementsByClassName('meta-key')[rowIndex + 1] as HTMLInputElement | undefined;
        nextKeyInput?.focus();
      });
    }
  }


  handleAutoFocus(index: number): void {
    if (this.autoFocusTimer) {
      clearTimeout(this.autoFocusTimer);
    }
    this.autoFocusTimer = setTimeout(() => {
      this.autoFocusTimer = undefined;
      this.selectRow(index, new MouseEvent('click'));
      const keyInput = document.getElementsByClassName('meta-key')[index] as HTMLInputElement | undefined;
      keyInput?.focus();
    }, 100);
  }

}
